import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { VARIANTS, KITCHEN_PACKAGES } from './constants';
import { CheckCircle, Save, Loader2, LogIn, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import { getUser } from '@/utils/auth';
import { initiateServicePayment } from '@/utils/razorpay';
import { ConsultationModal } from './ConsultationModal';

export const QuoteResult = ({ quote, estimateMode, onReset, formData }) => {
  const navigate = useNavigate();
  const user = getUser();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [consultationFee, setConsultationFee] = useState(25000); // Default fallback
  const [loadingFee, setLoadingFee] = useState(true);

  // Fetch consultation fee from API
  useEffect(() => {
    const fetchConsultationFee = async () => {
      try {
        const response = await apiClient.get('/services-pricing/interior-design');
        const fee = response.data?.consultation_fee?.price;
        if (fee) {
          setConsultationFee(fee);
        }
      } catch (error) {
        console.log('Using default consultation fee');
      } finally {
        setLoadingFee(false);
      }
    };
    fetchConsultationFee();
  }, []);

  // Handle consultation booking with payment
  const handleBookConsultation = async () => {
    // First check if user is logged in
    if (!user) {
      // Store quote for restoration after login
      localStorage.setItem('pending_consultation_quote', JSON.stringify(quote));
      toast.info('Please login or create an account to book a consultation');
      navigate('/login?redirect=/interior-design&action=book_consultation');
      return;
    }

    setProcessingPayment(true);
    
    try {
      await initiateServicePayment({
        amount: consultationFee, // Dynamic consultation fee from API
        currency: 'INR',
        description: `Interior Design Consultation - Quote ${quote.quote_id}`,
        userName: formData?.name || user?.name || '',
        userEmail: formData?.email || user?.email || '',
        userPhone: formData?.phone || user?.phone || '',
        metadata: {
          service_type: 'interior_consultation',
          quote_id: quote.quote_id,
          quote_total: quote.total_bill || quote.quote_total || quote.total_amount,
        },
        onSuccess: (response) => {
          toast.success('Payment successful! Now let\'s schedule your consultation.');
          setPaymentCompleted(true);
          setShowConsultationModal(true);
          setProcessingPayment(false);
        },
        onFailure: (error) => {
          toast.error(error.error || 'Payment failed. Please try again.');
          setProcessingPayment(false);
        },
        onDismiss: () => {
          toast.info('Payment cancelled');
          setProcessingPayment(false);
        },
      });
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Something went wrong. Please try again.');
      setProcessingPayment(false);
    }
  };

  if (!quote) return null;

  const variantInfo = VARIANTS.find(v => v.value === quote.variant);
  
  // Calculate budget exceedance
  const userBudget = quote.budget_provided || quote.user_budget;
  const quoteTotal = quote.total_bill || quote.quote_total || quote.total_amount || quote.summary?.total_amount;
  const isOverBudget = userBudget && quoteTotal && quoteTotal > userBudget;
  const exceedanceAmount = isOverBudget ? (quoteTotal - userBudget) : 0;
  const exceedancePercent = isOverBudget ? ((exceedanceAmount / userBudget) * 100).toFixed(1) : 0;
  
  // Scope optimization info (from Smart AI Estimator)
  const scopeOptimized = quote.scope_optimized || false;
  const scopeAdjustments = quote.scope_adjustments || [];
  const whatYouGet = quote.what_you_get || {};

  // Save quote to user profile
  const handleSaveQuote = async () => {
    if (!user) {
      // Store quote in localStorage and redirect to login
      localStorage.setItem('pending_quote', JSON.stringify(quote));
      toast.info('Please login to save your quote');
      navigate('/login?redirect=/interior-design&action=save_quote');
      return;
    }

    setSaving(true);
    try {
      await apiClient.post('/interior-design/quotes/save', {
        quote_id: quote.quote_id
      });
      setSaved(true);
      toast.success('Quote saved to your profile!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save quote. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Share quote via different methods
  const handleShare = async (method) => {
    const shareUrl = `${window.location.origin}/interior-design/quote/${quote.quote_id}`;
    const shareText = `Check out my interior design quote from ZenStudios - ₹${(quote.total_bill || quote.quote_total || quote.total_amount || quote.summary?.total_amount)?.toLocaleString('en-IN')}`;

    if (method === 'copy') {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Quote link copied to clipboard!');
    } else if (method === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank');
    } else if (method === 'email') {
      window.location.href = `mailto:?subject=My Interior Design Quote from ZenStudios&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`;
    }
  };

  // Download quote as PDF (simplified - opens print dialog)
  const handleDownload = () => {
    window.print();
  };

  // Helper to render kitchen room card with package features
  const renderKitchenRoom = (room) => {
    const pkgInfo = room.kitchen_package_info || 
      KITCHEN_PACKAGES.find(p => p.value === room.kitchen_package);
    
    // Build itemized costs for kitchen
    const itemizedCosts = [
      { item: 'Woodwork (Cabinets + Counter)', cost: room.woodwork_subtotal },
      { item: 'Hardware & Accessories', cost: room.hardware_cost },
    ];
    
    // Add electrical with profile light info if present
    if (room.electrical_cost > 0) {
      let electricalLabel = 'Electrical & Lighting';
      if (room.profile_lighting_cost > 0) {
        electricalLabel = 'Electrical & Lighting (incl. Profile Lights)';
      }
      itemizedCosts.push({ item: electricalLabel, cost: room.electrical_cost });
    }
    
    // Add Tall Uppers / Loft cost if present
    if (room.tall_uppers_cost > 0) {
      itemizedCosts.push({ item: 'Tall Uppers', cost: room.tall_uppers_cost });
    }
    if (room.loft_cost > 0) {
      itemizedCosts.push({ item: 'Loft Storage', cost: room.loft_cost });
    }
    
    if (room.false_ceiling_cost > 0) {
      itemizedCosts.push({ item: 'False Ceiling', cost: room.false_ceiling_cost });
    }
    
    if (room.actual_labor_cost > 0 || room.labor_cost > 0) {
      itemizedCosts.push({ item: 'Labor & Installation', cost: room.actual_labor_cost || room.labor_cost });
    }
    
    return (
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-primary text-lg">{room.room_name}</h4>
          {pkgInfo && (
            <span className="text-xs px-3 py-1 bg-primary/10 text-primary rounded-full font-medium">
              {pkgInfo.name}
            </span>
          )}
        </div>
        
        {/* Dimensions */}
        <p className="text-sm text-gray-500 mb-3">
          {room.frontage_length}ft running length × {room.height || 9.5}ft height
          {room.derived_area && <span className="ml-2">(Kitchen area: {room.derived_area} sqft)</span>}
          {room.tall_uppers && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">+ Tall Uppers</span>}
          {room.add_loft && <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">+ Loft</span>}
        </p>
        
        {/* Kitchen dimensions details (for Smart Generator) */}
        {room.dimensions && (
          <div className="mb-3 bg-gray-50 p-2 rounded text-xs text-gray-600">
            <span className="font-medium">Configuration: </span>
            Lower: {room.dimensions.lower_cabinets} | Upper: {room.dimensions.upper_cabinets} | Counter: {room.dimensions.counter_top}
          </div>
        )}

        {/* Itemized Costs Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
          {itemizedCosts.filter(item => item.cost > 0).map((item, iIdx) => (
            <div key={iIdx} className="bg-gray-50 p-3 rounded">
              <span className="text-gray-500 block text-xs">{item.item}</span>
              <span className="font-semibold">₹{item.cost?.toLocaleString('en-IN')}</span>
            </div>
          ))}
        </div>
        
        {/* Electrical breakdown tooltip (if profile lights included) */}
        {room.profile_lighting_cost > 0 && (
          <div className="mb-3 p-2 bg-amber-50 rounded-lg text-xs text-amber-800 border border-amber-200">
            <span className="font-medium">Electrical includes:</span> Base lighting + Profile lights (₹{room.profile_lighting_cost?.toLocaleString('en-IN')})
          </div>
        )}

        {/* Package Features */}
        {pkgInfo && pkgInfo.features && (
          <div className="border-t pt-3">
            <p className="text-sm font-medium text-gray-700 mb-2">Package Includes</p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-1">
              {(Array.isArray(pkgInfo.features) ? pkgInfo.features : Object.values(pkgInfo.features)).slice(0, 6).map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2 text-xs text-gray-600">
                  <CheckCircle className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
                  <span>{typeof feature === 'string' ? feature : feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Shutter Add-ons Note (if selected) */}
        {room.shutter_addons && (room.shutter_addons.glass || room.shutter_addons.traditional || room.shutter_addons.acrylic) && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
              <span className="font-medium">Shutter Add-ons Selected:</span>
              {room.shutter_addons.glass && ' Glass Shutters'}
              {room.shutter_addons.traditional && ' Traditional Shutters'}
              {room.shutter_addons.acrylic && ' Acrylic Shutters'}
              <br />
              <span className="italic">* To be configured at Design Center - not included in this quote</span>
            </p>
          </div>
        )}

        {/* Room Total */}
        <div className="mt-3 pt-3 border-t flex justify-between items-center">
          <span className="font-medium">Kitchen Total</span>
          <span className="font-bold text-primary text-lg">
            ₹{(room.room_total_before_buffers || room.room_total)?.toLocaleString('en-IN')}
          </span>
        </div>
      </div>
    );
  };

  // Helper to render non-kitchen room card (with sub-items support)
  const renderOtherRoom = (room) => {
    // Check if room has sub-items (new format - room-by-room mode)
    const hasSubItems = room.sub_items && room.sub_items.length > 0;
    // Check if room has items array (smart generator mode)
    const hasSmartItems = room.items && room.items.length > 0;
    
    // Build itemized costs from room breakdown fields
    const itemizedCosts = [
      { item: 'Woodwork', cost: room.woodwork_subtotal },
      { item: 'Hardware & Accessories', cost: room.hardware_cost },
    ];
    
    // Add electrical with breakdown info
    if (room.electrical_cost > 0) {
      let electricalLabel = 'Electrical & Lighting';
      if (room.profile_lighting_cost > 0) {
        electricalLabel = 'Electrical & Lighting (incl. Profile Lights)';
      }
      itemizedCosts.push({ item: electricalLabel, cost: room.electrical_cost, hasBreakdown: true });
    }
    
    // Add false ceiling if applicable
    if (room.false_ceiling_cost > 0) {
      itemizedCosts.push({ item: 'False Ceiling', cost: room.false_ceiling_cost });
    }
    
    // Add labor cost
    if (room.actual_labor_cost > 0 || room.labor_cost > 0) {
      itemizedCosts.push({ item: 'Labor & Installation', cost: room.actual_labor_cost || room.labor_cost });
    }
    
    // Calculate sum of itemized costs for verification
    const itemizedTotal = itemizedCosts.reduce((sum, item) => sum + (item.cost || 0), 0);
    
    return (
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-semibold text-primary text-lg">{room.room_name}</h4>
            {/* Show room area for Smart Generator results (derived_area is the actual room floor area) */}
            {room.derived_area && (
              <p className="text-sm text-gray-500">
                Room area: {room.derived_area} sqft
              </p>
            )}
          </div>
        </div>
        
        {/* Smart Generator items breakdown - show furniture items only */}
        {hasSmartItems && (
          <div className="mb-3 space-y-2">
            {room.items.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{item.item}</span>
                </div>
                <span className="font-semibold text-primary">
                  ₹{item.cost?.toLocaleString('en-IN')}
                </span>
              </div>
            ))}
            {/* Show additional costs not in items (False Ceiling, Labor) */}
            {room.false_ceiling_cost > 0 && (
              <div className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                <span className="font-medium">False Ceiling</span>
                <span className="font-semibold text-primary">
                  ₹{room.false_ceiling_cost?.toLocaleString('en-IN')}
                </span>
              </div>
            )}
            {(room.actual_labor_cost > 0 || room.labor_cost > 0) && (
              <div className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                <span className="font-medium">Labor & Installation</span>
                <span className="font-semibold text-primary">
                  ₹{(room.actual_labor_cost || room.labor_cost)?.toLocaleString('en-IN')}
                </span>
              </div>
            )}
          </div>
        )}
        
        {/* Sub-items list for Room-by-Room mode */}
        {hasSubItems && (
          <div className="mb-3 space-y-2">
            {room.sub_items.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{item.item_name || item.item_type}</span>
                  <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                    {item.frontage_length}ft × {item.height}ft
                  </span>
                </div>
                <span className="font-semibold text-primary">
                  ₹{item.item_total?.toLocaleString('en-IN')}
                </span>
              </div>
            ))}
          </div>
        )}
        
        {/* Cost breakdown grid - only show for Room-by-Room mode (not Smart Generator) */}
        {!hasSmartItems && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {itemizedCosts.filter(item => item.cost > 0).map((item, iIdx) => (
              <div key={iIdx} className="bg-gray-50 p-3 rounded">
                <span className="text-gray-500 block text-xs">{item.item}</span>
                <span className="font-semibold">₹{item.cost?.toLocaleString('en-IN')}</span>
              </div>
            ))}
          </div>
        )}
        
        {/* Electrical breakdown tooltip (if profile lights included) */}
        {room.profile_lighting_cost > 0 && (
          <div className="mt-2 p-2 bg-amber-50 rounded-lg text-xs text-amber-800 border border-amber-200">
            <span className="font-medium">Electrical includes:</span> Base lighting + Profile lights (₹{room.profile_lighting_cost?.toLocaleString('en-IN')})
          </div>
        )}
        
        <div className="mt-3 pt-3 border-t flex justify-between items-center">
          <span className="font-medium">Room Total</span>
          <span className="font-bold text-primary text-lg">
            ₹{(room.room_total_before_buffers || room.room_total || itemizedTotal)?.toLocaleString('en-IN')}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Quote Header */}
      <div className="bg-gradient-to-r from-primary to-green-600 text-white p-6 rounded-xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Your Quote is Ready!</h2>
            <p className="text-white/80 mt-1">Quote ID: {quote.quote_id}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-white/80">Total Estimate</p>
            <p className="text-3xl font-bold">
              ₹{(quote.total_bill || quote.quote_total || quote.total_amount || quote.summary?.total_amount)?.toLocaleString('en-IN')}
            </p>
          </div>
        </div>
      </div>

      {/* Budget Exceedance Notice - Friendly amber design */}
      {isOverBudget && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-amber-800 text-lg">Let&apos;s Fine-tune Your Quote Together!</h4>
              <p className="text-amber-700 text-sm mt-1">
                Your estimated quote of <span className="font-semibold">₹{quoteTotal?.toLocaleString('en-IN')}</span> is about <span className="font-bold text-amber-800">{exceedancePercent}%</span> above your budget of <span className="font-semibold">₹{userBudget?.toLocaleString('en-IN')}</span>.
              </p>
              {scopeOptimized && (
                <p className="text-amber-700 text-sm mt-2">
                  <span className="font-medium">We&apos;ve already optimized your scope</span> to get closer to your budget while maintaining quality.
                </p>
              )}
              <p className="text-amber-700 text-sm mt-2">
                While our calculator provides a solid starting point, computers sometimes miss the human touch. 
                <span className="font-medium"> Our design team is more than happy to work with you</span> to customize the scope, 
                explore alternatives, and bring your dream home within your budget — with a smile! 😊
              </p>
              <div className="mt-3 bg-amber-100 rounded-lg p-3">
                <p className="text-amber-800 text-sm font-medium">💡 What happens next?</p>
                <ul className="text-amber-700 text-xs mt-1 space-y-1 list-disc list-inside">
                  <li>Schedule a free consultation with our design team</li>
                  <li>We&apos;ll review your requirements together</li>
                  <li>Get a customized quote that fits your budget perfectly</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scope Optimization Notice - when scope was adjusted to fit budget */}
      {scopeOptimized && !isOverBudget && scopeAdjustments.length > 0 && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-blue-800 text-lg">Optimized for Your Budget!</h4>
              <p className="text-blue-700 text-sm mt-1">
                We&apos;ve adjusted your scope to fit within ₹{userBudget?.toLocaleString('en-IN')}. Here&apos;s what was fine-tuned:
              </p>
              <ul className="text-blue-700 text-xs mt-2 space-y-1 list-disc list-inside">
                {scopeAdjustments.slice(0, 4).map((adj, idx) => (
                  <li key={idx}>{adj}</li>
                ))}
              </ul>
              <p className="text-blue-600 text-xs mt-2 italic">
                💡 These can be added back during your design consultation if budget allows.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Within Budget Success */}
      {userBudget && !isOverBudget && quote.margin_available > 0 && (
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-green-800 text-lg">Within Budget!</h4>
              <p className="text-green-700 text-sm mt-1">
                Great news! Your quote is within your budget with <span className="font-bold text-green-800">₹{quote.margin_available?.toLocaleString('en-IN')}</span> to spare.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Variant Badge */}
      {variantInfo && (
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${variantInfo.color}`}>
          <span className="font-semibold">{variantInfo.label} Style</span>
          <span className="text-sm">• {variantInfo.materials}</span>
        </div>
      )}

      {/* Room Breakdowns */}
      {quote.room_breakdowns && quote.room_breakdowns.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-bold">Room-by-Room Breakdown</h3>
          {quote.room_breakdowns.map((room, rIdx) => (
            <div key={rIdx}>
              {room.room_type === 'kitchen' ? renderKitchenRoom(room) : renderOtherRoom(room)}
            </div>
          ))}
        </div>
      )}

      {/* Cost Summary Table */}
      <div className="bg-white p-4 md:p-6 rounded-lg">
        <h3 className="text-lg font-bold mb-4">Cost Summary</h3>
        <div className="space-y-2">
          {quote?.woodwork_subtotal !== undefined && (
            <div className="flex justify-between py-2 border-b">
              <span>Materials</span>
              <span className="font-mono">₹{quote.woodwork_subtotal?.toLocaleString('en-IN')}</span>
            </div>
          )}
          {quote?.hardware_cost !== undefined && (
            <div className="flex justify-between py-2 border-b">
              <span>Hardware & Accessories</span>
              <span className="font-mono">₹{quote.hardware_cost?.toLocaleString('en-IN')}</span>
            </div>
          )}
          {quote?.electrical_cost !== undefined && (
            <div className="flex justify-between py-2 border-b">
              <span>Electrical & Lighting</span>
              <span className="font-mono">₹{quote.electrical_cost?.toLocaleString('en-IN')}</span>
            </div>
          )}
          {quote?.false_ceiling_cost !== undefined && quote.false_ceiling_cost > 0 && (
            <div className="flex justify-between py-2 border-b">
              <span>False Ceiling</span>
              <span className="font-mono">₹{quote.false_ceiling_cost?.toLocaleString('en-IN')}</span>
            </div>
          )}
          {quote?.labor_cost !== undefined && quote.labor_cost > 0 && (
            <div className="flex justify-between py-2 border-b">
              <span>Labor & Installation</span>
              <span className="font-mono">₹{quote.labor_cost?.toLocaleString('en-IN')}</span>
            </div>
          )}
          {quote?.management_fee !== undefined && quote.management_fee > 0 && (
            <div className="flex justify-between py-2 border-b">
              <span>Management Fee</span>
              <span className="font-mono">₹{quote.management_fee?.toLocaleString('en-IN')}</span>
            </div>
          )}
          {quote?.misc_costs !== undefined && quote.misc_costs > 0 && (
            <div className="flex justify-between py-2 border-b">
              <span>Miscellaneous Expenses</span>
              <span className="font-mono">₹{quote.misc_costs?.toLocaleString('en-IN')}</span>
            </div>
          )}

          {/* Budget mode summary */}
          {quote?.summary && (
            <>
              <div className="flex justify-between py-2 border-b">
                <span>Room Costs (Woodwork + Materials)</span>
                <span className="font-mono">₹{quote.summary.total_room_costs?.toLocaleString('en-IN')}</span>
              </div>
              {quote.summary.wastage_buffer !== undefined && quote.summary.wastage_buffer > 0 && (
                <div className="flex justify-between py-2 border-b">
                  <span>Wastage Buffer (10%)</span>
                  <span className="font-mono">₹{quote.summary.wastage_buffer?.toLocaleString('en-IN')}</span>
                </div>
              )}
              {quote.summary.management_fee !== undefined && quote.summary.management_fee > 0 && (
                <div className="flex justify-between py-2 border-b">
                  <span>Management Fee (15%)</span>
                  <span className="font-mono">₹{quote.summary.management_fee?.toLocaleString('en-IN')}</span>
                </div>
              )}
              {quote.summary.misc_costs !== undefined && quote.summary.misc_costs > 0 && (
                <div className="flex justify-between py-2 border-b">
                  <span>Miscellaneous (5%)</span>
                  <span className="font-mono">₹{quote.summary.misc_costs?.toLocaleString('en-IN')}</span>
                </div>
              )}
            </>
          )}

          {/* Total */}
          <div className="flex justify-between py-2 border-b">
            <span className="font-semibold">Total Project Value</span>
            <span className="font-mono font-semibold">
              ₹{(quote.total_project_value || quote.summary?.total_project_value)?.toLocaleString('en-IN')}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b">
            <span>GST (18%)</span>
            <span className="font-mono">
              ₹{(quote.gst || quote.summary?.gst)?.toLocaleString('en-IN')}
            </span>
          </div>
          <div className="flex justify-between py-3 bg-primary/10 rounded-lg px-3 mt-2">
            <span className="text-lg font-bold">Total Project Cost</span>
            <span className="text-lg font-bold text-primary">
              ₹{(quote.total_bill || quote.quote_total || quote.total_amount || quote.summary?.total_amount)?.toLocaleString('en-IN')}
            </span>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
        <p className="font-semibold mb-1">Important Note:</p>
        <p>{quote.disclaimer || 'This quote is valid for 30 days. Final costs may vary based on site conditions and design modifications.'}</p>
      </div>

      {/* Book Consultation CTA */}
      <div className="bg-gradient-to-r from-primary/5 to-primary/10 border-2 border-primary/20 rounded-xl p-6">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="flex-shrink-0 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Calendar className="w-8 h-8 text-primary" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Ready to Bring Your Vision to Life?
            </h3>
            <p className="text-gray-600 mb-3">
              Book a personalized consultation with our expert design team. We&apos;ll walk through your requirements, 
              customize the quote to your exact needs, and help you visualize your dream space.
            </p>
            <ul className="text-sm text-gray-600 space-y-1 mb-4">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                <span>1-hour personalized design consultation</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                <span>Detailed material samples and finish options</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                <span>Custom 3D visualization of your space</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                <span>₹{consultationFee?.toLocaleString('en-IN')} fully adjustable against final project</span>
              </li>
            </ul>
          </div>
          <div className="flex flex-col items-center gap-3">
            <div className="text-center">
              <p className="text-sm text-gray-500">Consultation Fee</p>
              <p className="text-3xl font-bold text-primary">
                {loadingFee ? '...' : `₹${consultationFee?.toLocaleString('en-IN')}`}
              </p>
              <p className="text-xs text-gray-500">Adjustable in final quote</p>
            </div>
            <Button
              className="bg-primary hover:bg-primary/90 text-white px-8 py-3 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all"
              data-testid="book-consultation-btn"
              onClick={handleBookConsultation}
              disabled={processingPayment}
            >
              {processingPayment ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Book Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
            <p className="text-xs text-gray-500 text-center">
              Or call us: +91 87904 60259
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 pt-4">
        {/* Save Quote Button */}
        <Button
          onClick={handleSaveQuote}
          disabled={saving || saved}
          className="rounded-none bg-[#15803D] hover:bg-[#166534] text-white flex-1 sm:flex-none"
          data-testid="save-quote-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : saved ? (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Saved to Profile
            </>
          ) : user ? (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save to Profile
            </>
          ) : (
            <>
              <LogIn className="w-4 h-4 mr-2" />
              Login to Save
            </>
          )}
        </Button>

        {/* Share Button - Hidden until feature flags implemented */}
        {/* <div className="relative group">
          <Button
            variant="outline"
            className="rounded-none border-[#15803D] text-[#15803D] hover:bg-[#15803D]/5"
            data-testid="share-quote-btn"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
          <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg p-2 hidden group-hover:block z-10 min-w-[150px]">
            <button 
              onClick={() => handleShare('copy')} 
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded"
            >
              Copy Link
            </button>
            <button 
              onClick={() => handleShare('whatsapp')} 
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded"
            >
              WhatsApp
            </button>
            <button 
              onClick={() => handleShare('email')} 
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded"
            >
              Email
            </button>
          </div>
        </div> */}

        {/* Download Button - Hidden until feature flags implemented */}
        {/* <Button
          variant="outline"
          onClick={handleDownload}
          className="rounded-none"
          data-testid="download-quote-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Download PDF
        </Button> */}

        {/* New Quote Button */}
        {onReset && (
          <Button
            variant="ghost"
            onClick={onReset}
            className="rounded-none text-gray-600"
            data-testid="new-quote-btn"
          >
            Get New Quote
          </Button>
        )}
      </div>

      {/* Login Prompt for Guests */}
      {!user && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Want to save this quote?</strong> Create a free account to save quotes, track your projects, and get exclusive offers.
          </p>
        </div>
      )}

      {/* Consultation Modal - Shows after successful payment */}
      <ConsultationModal
        isOpen={showConsultationModal}
        onClose={() => setShowConsultationModal(false)}
        quoteId={quote.quote_id}
        formData={formData}
      />
    </div>
  );
};

export default QuoteResult;
